import * as RadixPopover from '@radix-ui/react-popover';
import { memo, useCallback, useRef, useState } from 'react';

import { type SearchBoardsResponse, type SearchSavedFiltersResponse, type SuggestionsResponse } from '@air/api/types';
import { Command } from '@air/cmdk';

import { SearchCommandBoardList } from '~/components/SearchCommand/SearchCommandBoardList';
import {
  SearchCommandClearButton,
  type SearchCommandClearButtonProps,
} from '~/components/SearchCommand/SearchCommandClearButton';
import { SearchCommandInput } from '~/components/SearchCommand/SearchCommandInput';
import { SearchCommandSavedFilterList } from '~/components/SearchCommand/SearchCommandSavedFilterList';
import { SearchCommandSuggestionList } from '~/components/SearchCommand/SearchCommandSuggestionList';
import { SearchCommandTrigger } from '~/components/SearchCommand/SearchCommandTrigger';

export type MobileSearchCommandProps = {
  defaultOpen?: boolean;
  filtersCount?: number;
  hasFilterBar?: boolean;
  onBoardClick?: (board: SearchBoardsResponse) => void;
  onOpenChange?: (isOpen: boolean) => void;
  onSavedFilterSelect?: (savedFilter: SearchSavedFiltersResponse) => void;
  onSearchChange?: (search: string) => void;
  onSuggestionClick?: (suggestion: string) => void;
  placeholder?: string;
  search?: string;
  suggestions?: SuggestionsResponse;
  onClear?: SearchCommandClearButtonProps['onClick'];
};

export const MobileSearchCommand = memo(
  ({
    defaultOpen,
    filtersCount,
    onBoardClick: _onBoardClick,
    onClear,
    onOpenChange,
    onSavedFilterSelect: _onSavedFilterSelect,
    onSearchChange,
    onSuggestionClick: _onSuggestionClick,
    placeholder,
    search,
    suggestions,
  }: MobileSearchCommandProps) => {
    const [isOpen, setIsOpen] = useState(defaultOpen ?? false);
    const [searchValue, setSearchValue] = useState(search);
    const inputRef = useRef<HTMLInputElement>(null);

    const onOpen = useCallback(() => {
      setIsOpen(true);
      onOpenChange?.(true);
    }, [onOpenChange]);

    const onBoardClick = useCallback(
      (board: SearchBoardsResponse) => {
        setIsOpen(false);
        _onBoardClick?.(board);
      },
      [_onBoardClick],
    );

    const onSavedFilterSelect = useCallback(
      (savedFilter: SearchSavedFiltersResponse) => {
        setIsOpen(false);
        _onSavedFilterSelect?.(savedFilter);
      },
      [_onSavedFilterSelect],
    );

    const onSuggestionClick = useCallback(
      (suggestion: string) => {
        setIsOpen(false);
        setSearchValue(suggestion);
        _onSuggestionClick?.(suggestion);
      },
      [_onSuggestionClick],
    );

    const onValueChange = useCallback(
      (value: string) => {
        setSearchValue(value);
        onSearchChange?.(value);
      },
      [onSearchChange],
    );

    return (
      <Command className="w-full" data-testid="SEARCH_COMMAND" shouldFilter={false} defaultSearch={search}>
        <RadixPopover.Root open={isOpen} onOpenChange={setIsOpen} modal>
          <div className="relative">
            <RadixPopover.Trigger asChild>
              <SearchCommandTrigger
                count={filtersCount}
                data-testid="SEARCH_COMMAND_TRIGGER"
                onClick={onOpen}
                placeholder={placeholder}
              />
            </RadixPopover.Trigger>
            {!!onClear && <SearchCommandClearButton onClick={onClear} />}
          </div>

          <RadixPopover.Portal>
            <RadixPopover.Content
              className="data-[state=closed]:fade-out data-[state=open]:fade-in pointer-events-none relative top-[calc(var(--radix-popover-trigger-height)*-1)] left-0 flex h-[calc(var(--radix-popover-content-available-height)+var(--radix-popover-trigger-height))] w-screen flex-col overflow-hidden transition-all data-[state=closed]:animate-out data-[state=open]:animate-in"
              data-testid="SEARCH_COMMAND_CONTENT"
              onOpenAutoFocus={(e) => {
                e.preventDefault();
                inputRef.current?.focus();
              }}
            >
              <RadixPopover.Close asChild>
                <div className="pointer-events-auto flex flex-col border-b border-b-grey-5 bg-grey-1 px-4 pb-2">
                  <div className="relative w-full">
                    <SearchCommandInput
                      ref={inputRef}
                      defaultValue={searchValue}
                      className="m-0 rounded-8 border border-grey-5"
                      count={filtersCount}
                      onValueChange={onValueChange}
                      placeholder={placeholder}
                      value={searchValue}
                    />
                    {!!onClear && <SearchCommandClearButton onClick={onClear} />}
                  </div>
                </div>
              </RadixPopover.Close>
              <div className="pointer-events-auto grow overflow-y-auto bg-grey-2">
                {isOpen && (
                  <Command.List className="flex flex-col gap-3 px-4 py-3" data-testid="SEARCH_COMMAND_LIST">
                    {!!onSuggestionClick && (
                      <SearchCommandSuggestionList
                        onSelect={onSuggestionClick}
                        suggestions={suggestions?.suggestions}
                      />
                    )}

                    {!!suggestions?.savedFilters.length && !!onSavedFilterSelect && (
                      <SearchCommandSavedFilterList
                        onSelect={onSavedFilterSelect}
                        savedFilters={suggestions.savedFilters}
                      />
                    )}

                    {!!suggestions?.boards.length && (
                      <SearchCommandBoardList boards={suggestions.boards} onBoardClick={onBoardClick} />
                    )}
                  </Command.List>
                )}
              </div>
            </RadixPopover.Content>
          </RadixPopover.Portal>
        </RadixPopover.Root>
      </Command>
    );
  },
);

MobileSearchCommand.displayName = 'MobileSearchCommand';
